import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * CTA — new variants (pill-bar · social-pill-row · mountain-overlay ·
 * video-modal-cta · phone-on-sunset · balloons-asymmetric)
 * ════════════════════════════════════════════════════════════════════════ */
export function CtaExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const layout = block.layout;
  const buttons = c.buttons || [];

  if (layout === 'pill-bar') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-5" style={{ background: theme.accent || '#2DD4BF' }}>
        <div className="max-w-6xl mx-auto text-center">
          <span className="text-white font-bold uppercase tracking-wider text-base md:text-lg">{c.title || buttons[0]?.text || 'Get Started for Free'}</span>
        </div>
      </section>
    );
  }

  if (layout === 'social-pill-row') {
    const socials = c.socials || ['twitter', 'facebook', 'instagram', 'behance', 'dribbble'];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16 text-center" style={{ background: '#3B1F8B', color: '#fff' }}>
        <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }}>{c.title || 'Better Landing for Your Startup'}</h2>
        <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 opacity-80 max-w-xl mx-auto" placeholder='Tagline (optional)' />
        <div className="mt-8 flex justify-center flex-wrap gap-3">
          {socials.map((s,i) => <button key={i} className="px-5 py-2 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-wider font-bold capitalize">{s}</button>)}
        </div>
      </section>
    );
  }

  if (layout === 'mountain-overlay') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative px-6 py-32 text-center overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-b from-violet-700 via-violet-500 to-rose-300" />}
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 text-white max-w-2xl mx-auto">
          <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-5xl"  testid={tid('title')}>{c.title || 'Easy to setup. Easy to maintain.'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-6 opacity-90" placeholder='Tagline (optional)' />
          {buttons.length > 0 && <div className="mt-8 flex justify-center gap-3">{buttons.map((b,i)=><Btn block={block} onEdit={onEdit} key={i} text={b.text} link={b.link} primary={i===0} buttonIndex={i} color={b.color} theme={{ ...theme, ink: '#fff' }} />)}</div>}
        </div>
      </section>
    );
  }

  if (layout === 'video-modal-cta') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative px-6 py-24 text-center overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-violet-600" />}
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative z-10 text-white max-w-2xl mx-auto">
          <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-3xl md:text-5xl"  testid={tid('title')}>{c.title || 'Create a beautiful, responsive website in under 60 minutes'}</H1>
          <button className="mt-10 w-16 h-16 rounded-full bg-white/95 flex items-center justify-center mx-auto hover:scale-110 transition-transform shadow-xl" aria-label="Play">
            <span className="ml-1 w-0 h-0 border-l-[14px] border-l-violet-600 border-y-[10px] border-y-transparent" />
          </button>
        </div>
      </section>
    );
  }

  if (layout === 'phone-on-sunset') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative px-6 py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #FBA5C0, #A78BFA, #4F46E5)' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center text-white">
          <div>
            <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={{ ink: '#fff' }}  testid={tid('eyebrow')}>{c.eyebrow || 'Try It Risk Free'}</Eyebrow>
            <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-5xl"  testid={tid('title')}>{c.title || 'Get Started for Free'}</H1>
            <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 opacity-90" placeholder='Tagline (optional)' />
            <div className="mt-6 flex gap-2 max-w-sm">
              <input type="email" placeholder="your@email.com" className="flex-1 px-4 py-3 rounded-full text-sm text-gray-900" />
              <button className="px-5 py-3 rounded-full text-sm font-bold uppercase text-white" style={{ background: theme.accent || '#0E0E23' }}>{buttons[0]?.text || 'Sign Up'}</button>
            </div>
          </div>
          <div className="relative h-72">
            {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-contain" /> : <div className="absolute right-0 bottom-0 w-48 h-72 bg-gradient-to-br from-pink-300 to-violet-400 rounded-3xl mx-auto" />}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'balloons-asymmetric') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="grid grid-cols-1 md:grid-cols-2 min-h-[400px]" style={{ background: theme.bg, color: theme.ink }}>
        <div className="px-8 md:px-16 py-16 flex flex-col justify-center">
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Start Working Faster'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 text-sm opacity-75" placeholder='Tagline (optional)' />
          {buttons.length > 0 && <div className="mt-6"><Btn block={block} onEdit={onEdit} text={buttons[0].text} link={buttons[0].link} primary buttonIndex={0} color={buttons[0].color} theme={theme} /></div>}
        </div>
        <div>{c.image_url ? <SafeImg src={c.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-sky-300 to-indigo-400" />}</div>
      </section>
    );
  }

  if (layout === 'app-store-badges') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16 text-center" style={{ background: theme.bg, color: theme.ink }}>
        <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Better landing for your startup'}</H1>
        <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 max-w-xl mx-auto opacity-75" placeholder='Tagline (optional)' />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {(c.stores || ['App Store', 'Google Play', 'Microsoft Store']).map((s,i) => (
            <a key={i} href="#" className="inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-current/20 hover:border-current/40 transition-all" style={{ borderRadius: theme.radius || 12 }}>
              <span className="w-8 h-8 rounded-md bg-gradient-to-br from-slate-800 to-black flex items-center justify-center text-white text-xs">▶</span>
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-wider opacity-60">Download on</span>
                <span className="block text-sm font-bold">{s}</span>
              </span>
            </a>
          ))}
        </div>
      </section>
    );
  }

  if (layout === 'slim-promo-bar') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-3" style={{ background: '#3B1F8B' }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-white">
          <p className="text-sm font-bold">{c.title || buttons[0]?.text || 'Twenty Five Awesome Startup Pages'}</p>
          {buttons[0] && (
            <a href={buttons[0].link || '#'} className="px-5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: theme.accent || '#2DD4BF', color: '#0E0E23' }}>{buttons[0].text || "I'm Ready Now"}</a>
          )}
        </div>
      </section>
    );
  }

  if (layout === 'integration-cards-cta') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-4xl mx-auto text-center">
          <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Create Something Awesome'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-3 max-w-xl mx-auto opacity-70" placeholder='Tagline (optional)' />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {(c.integrations || [
              { name: 'Dropbox', icon: '📦', cta: 'Connect' },
              { name: 'GitHub', icon: '⚙', cta: 'Connect' },
            ]).slice(0,2).map((it,i) => (
              <div key={i} className="border border-current/10 rounded-2xl p-6 flex items-center justify-between" style={{ borderRadius: theme.radius || 16 }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-current/5 flex items-center justify-center text-2xl">{it.icon}</div>
                  <div className="text-left">
                    <p className="font-bold">{it.name}</p>
                    <p className="text-xs opacity-60">{it.description || 'Sync in seconds.'}</p>
                  </div>
                </div>
                <button className="px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-white" style={{ background: theme.accent || '#2DD4BF' }}>{it.cta || 'Connect'}</button>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'sunset-promo-card') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg }}>
        <div className="max-w-4xl mx-auto relative rounded-2xl overflow-hidden" style={{ borderRadius: theme.radius || 16 }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #FB923C, #F472B6, #A78BFA)' }} />
          {c.image_url && <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
          <div className="relative p-10 md:p-16 text-center text-white">
            <H1 block={block} onEdit={onEdit} field='title' theme={{ ...theme, ink: '#fff' }} size="text-4xl md:text-5xl"  testid={tid('title')}>{c.title || 'Suit up your startup'}</H1>
            <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 opacity-90 max-w-lg mx-auto" placeholder='Tagline (optional)' />
            {buttons.length > 0 && <div className="mt-8 flex flex-wrap justify-center gap-3">{buttons.map((b,i) => <Btn block={block} onEdit={onEdit} key={i} text={b.text} link={b.link} primary={i===0} buttonIndex={i} color={b.color} theme={{ ...theme, ink: '#fff' }} />)}</div>}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'white-bordered-card') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-3xl mx-auto border-2 rounded-2xl p-10 md:p-16 text-center" style={{ borderColor: theme.accent || '#2DD4BF', borderRadius: theme.radius || 16 }}>
          <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
          <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Get Startup Framework'}</H1>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 opacity-75 max-w-md mx-auto" placeholder='Tagline (optional)' />
          {buttons.length > 0 && (
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Btn block={block} onEdit={onEdit} text={buttons[0].text || 'Get Started'} link={buttons[0].link} primary buttonIndex={0} color={buttons[0].color} theme={theme} />
              {buttons[1] && <a href={buttons[1].link || '#'} className="text-sm font-bold uppercase tracking-wider underline opacity-75" style={{ color: theme.accent }}>{buttons[1].text}</a>}
            </div>
          )}
        </div>
      </section>
    );
  }

  if (layout === 'video-strip-vertical') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="grid grid-cols-1 md:grid-cols-3" style={{ background: theme.bg, color: theme.ink }}>
        <div className="md:col-span-1 relative min-h-[280px] overflow-hidden">
          {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-rose-400 to-pink-600" />}
        </div>
        <div className="md:col-span-2 flex items-center px-8 md:px-12 py-12">
          <div>
            <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow || 'How It Works'}</Eyebrow>
            <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Watch Our Video'}</H1>
            <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-3 opacity-75" placeholder='Tagline (optional)' />
            <button className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-full text-sm font-bold uppercase tracking-wider border border-current/30 hover:border-current/60 transition-all">
              <span className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white"><span className="ml-1 w-0 h-0 border-l-[10px] border-l-white border-y-[7px] border-y-transparent" /></span>
              <span>Watch the video</span>
            </button>
          </div>
        </div>
      </section>
    );
  }
  return null;
}
